export type DocumentSpeechStatus = 'idle' | 'playing' | 'paused' | 'ended';

export type DocumentSpeechPreferences = {
  rate: number;
  volume: number;
  lang: string;
  voiceURI: string | null;
};

export type DocumentSpeechSourceRange = {
  from: number;
  to: number;
};

export type DocumentSpeechSpokenPlainRange = {
  from: number;
  to: number;
};

export type DocumentSpeechState = {
  status: DocumentSpeechStatus;
  preferences: DocumentSpeechPreferences;
  sourceRange: DocumentSpeechSourceRange | null;
  spokenPlainRange: DocumentSpeechSpokenPlainRange | null;
};

export type SpeakDocumentTextOptions = {
  sourceRange?: DocumentSpeechSourceRange;
};

export type SpeechChunk = {
  text: string;
  plainFrom: number;
  plainTo: number;
};

type DocumentSpeechListener = (state: DocumentSpeechState) => void;

const DEFAULT_MAX_CHUNK_LENGTH = 280;
export const DOCUMENT_SPEECH_STORAGE_KEY = 'scribane-document-speech';

export const DEFAULT_DOCUMENT_SPEECH_PREFERENCES: DocumentSpeechPreferences = {
  rate: 1,
  volume: 1,
  lang: 'en-US',
  voiceURI: null,
};

export const DOCUMENT_SPEECH_RATE_PRESETS = [0.5, 1, 1.25, 1.5, 2] as const;

export type DocumentSpeechRatePreset = (typeof DOCUMENT_SPEECH_RATE_PRESETS)[number];

export const formatDocumentSpeechRateLabel = (rate: number): string => {
  const rounded = Math.round(rate * 100) / 100;
  return `x${rounded}`;
};

export const getNextDocumentSpeechRatePreset = (rate: number): DocumentSpeechRatePreset => {
  const exactIndex = DOCUMENT_SPEECH_RATE_PRESETS.findIndex(
    (preset) => Math.abs(preset - rate) < 0.001,
  );
  if (exactIndex >= 0) {
    return DOCUMENT_SPEECH_RATE_PRESETS[
      (exactIndex + 1) % DOCUMENT_SPEECH_RATE_PRESETS.length
    ]!;
  }
  const nextHigher = DOCUMENT_SPEECH_RATE_PRESETS.find((preset) => preset > rate + 0.001);
  return nextHigher ?? DOCUMENT_SPEECH_RATE_PRESETS[0]!;
};

const listeners = new Set<DocumentSpeechListener>();
let status: DocumentSpeechStatus = 'idle';
let preferences: DocumentSpeechPreferences = loadPreferences();
let chunks: SpeechChunk[] = [];
let nextChunkIndex = 0;
let sourceRange: DocumentSpeechSourceRange | null = null;
let spokenPlainRange: DocumentSpeechSpokenPlainRange | null = null;
let speakSessionId = 0;

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

function loadPreferences(): DocumentSpeechPreferences {
  if (typeof window === 'undefined') {
    return { ...DEFAULT_DOCUMENT_SPEECH_PREFERENCES };
  }
  try {
    const raw = window.localStorage.getItem(DOCUMENT_SPEECH_STORAGE_KEY);
    if (!raw) {
      return { ...DEFAULT_DOCUMENT_SPEECH_PREFERENCES };
    }
    const parsed = JSON.parse(raw) as Partial<DocumentSpeechPreferences>;
    return normalizePreferences({
      ...DEFAULT_DOCUMENT_SPEECH_PREFERENCES,
      ...parsed,
    });
  } catch {
    return { ...DEFAULT_DOCUMENT_SPEECH_PREFERENCES };
  }
}

function persistPreferences(next: DocumentSpeechPreferences): void {
  if (typeof window === 'undefined') {
    return;
  }
  try {
    window.localStorage.setItem(DOCUMENT_SPEECH_STORAGE_KEY, JSON.stringify(next));
  } catch {
    // ignore quota / private mode
  }
}

export function normalizePreferences(
  input: DocumentSpeechPreferences,
): DocumentSpeechPreferences {
  return {
    rate: clamp(Number(input.rate) || 1, 0.5, 2),
    volume: clamp(Number(input.volume) || 0, 0, 1),
    lang: typeof input.lang === 'string' && input.lang ? input.lang : 'en-US',
    voiceURI: typeof input.voiceURI === 'string' && input.voiceURI ? input.voiceURI : null,
  };
}

const getState = (): DocumentSpeechState => ({
  status,
  preferences,
  sourceRange,
  spokenPlainRange,
});

const notifyListeners = (): void => {
  const state = getState();
  for (const listener of listeners) {
    listener(state);
  }
};

const setStatus = (next: DocumentSpeechStatus): void => {
  if (status === next) {
    return;
  }
  status = next;
  if (next === 'idle' || next === 'ended') {
    spokenPlainRange = null;
  }
  if (next === 'idle') {
    sourceRange = null;
  }
  notifyListeners();
};

export const isDocumentSpeechSupported = (): boolean =>
  typeof window !== 'undefined' &&
  typeof window.speechSynthesis !== 'undefined' &&
  typeof window.SpeechSynthesisUtterance !== 'undefined';

export const getDocumentSpeechStatus = (): DocumentSpeechStatus => status;

export const getDocumentSpeechPreferences = (): DocumentSpeechPreferences => preferences;

export const isDocumentSpeechPlaying = (): boolean => status === 'playing';

export const isDocumentSpeechActive = (): boolean =>
  status === 'playing' || status === 'paused';

export const isDocumentSpeechBarVisible = (): boolean => status !== 'idle';

export const subscribeDocumentSpeech = (listener: DocumentSpeechListener): (() => void) => {
  listeners.add(listener);
  listener(getState());
  return () => {
    listeners.delete(listener);
  };
};

export const listDocumentSpeechVoices = (): SpeechSynthesisVoice[] => {
  if (!isDocumentSpeechSupported()) {
    return [];
  }
  return window.speechSynthesis.getVoices();
};

const DOCUMENT_SPEECH_NOVELTY_VOICE_PATTERN =
  /\b(bells|bubbles|bad news|bahh|boing|cellos|good news|hysterical|junior|kathy|organ|pipes|princess|ralph|reed|trinoids|whisper|zarvox|albert|bahh|deranged|eddy|flo|grandma|grandpa|jester|superstar|shelley|sang|tessa compact|victoria compact)\b/i;

const DOCUMENT_SPEECH_LOW_QUALITY_VOICE_PATTERN =
  /\b(espeak|eloquence|compact|robot|novelty)\b/i;

const DOCUMENT_SPEECH_HIGH_QUALITY_VOICE_PATTERN =
  /\b(natural|neural|premium|enhanced|online|google|microsoft|samantha|daniel|karen|moira|monica|paulina|jorge|thomas|amelie|anna|helena|diego)\b/i;

export const scoreDocumentSpeechVoice = (
  voice: SpeechSynthesisVoice,
  lang: string,
): number => {
  const normalizedLang = lang.toLowerCase().replace('_', '-');
  const prefix = normalizedLang.split('-')[0] ?? normalizedLang;
  const voiceLang = voice.lang.toLowerCase().replace('_', '-');
  const voiceName = voice.name.toLowerCase();
  let score = 0;

  if (voiceLang === normalizedLang) {
    score += 100;
  } else if (voiceLang.startsWith(`${prefix}-`) || voiceLang === prefix) {
    score += 60;
  } else if (voiceLang.startsWith(prefix)) {
    score += 40;
  } else {
    return Number.NEGATIVE_INFINITY;
  }

  if (DOCUMENT_SPEECH_NOVELTY_VOICE_PATTERN.test(voiceName)) {
    score -= 200;
  }
  if (DOCUMENT_SPEECH_LOW_QUALITY_VOICE_PATTERN.test(voiceName)) {
    score -= 80;
  }
  if (/\b(natural|neural)\b/i.test(voiceName)) {
    score += 70;
  }
  if (/\b(premium|enhanced)\b/i.test(voiceName)) {
    score += 45;
  }
  if (/\b(google|microsoft|online)\b/i.test(voiceName)) {
    score += 35;
  }
  if (DOCUMENT_SPEECH_HIGH_QUALITY_VOICE_PATTERN.test(voiceName)) {
    score += 20;
  }
  if (!voice.localService) {
    score += 15;
  }
  if (voice.default) {
    score += 5;
  }
  return score;
};

export const rankDocumentSpeechVoices = (
  voices: SpeechSynthesisVoice[],
  lang: string,
): SpeechSynthesisVoice[] =>
  [...voices].sort(
    (left, right) =>
      scoreDocumentSpeechVoice(right, lang) - scoreDocumentSpeechVoice(left, lang),
  );

export const filterDocumentSpeechVoicesForLang = (
  voices: SpeechSynthesisVoice[],
  lang: string,
): SpeechSynthesisVoice[] => {
  const prefix = lang.toLowerCase().split('-')[0];
  const matching = voices.filter((voice) => voice.lang.toLowerCase().startsWith(prefix));
  const candidates = matching.length > 0 ? matching : voices;
  return rankDocumentSpeechVoices(candidates, lang).filter(
    (voice) => scoreDocumentSpeechVoice(voice, lang) > Number.NEGATIVE_INFINITY,
  );
};

export const chunkDocumentSpeechText = (
  text: string,
  maxChunkLength: number = DEFAULT_MAX_CHUNK_LENGTH,
): string[] => chunkDocumentSpeechTextWithOffsets(text, maxChunkLength).map((chunk) => chunk.text);

export const chunkDocumentSpeechTextWithOffsets = (
  text: string,
  maxChunkLength: number = DEFAULT_MAX_CHUNK_LENGTH,
): SpeechChunk[] => {
  const normalized = text.replace(/\r\n/g, '\n');
  const leadingWhitespace = normalized.match(/^\s*/)?.[0].length ?? 0;
  const trimmed = normalized.trim();
  if (!trimmed) {
    return [];
  }

  const chunks: SpeechChunk[] = [];

  const pushExact = (value: string, absoluteFrom: number): void => {
    if (!value) {
      return;
    }
    chunks.push({
      text: value,
      plainFrom: absoluteFrom,
      plainTo: absoluteFrom + value.length,
    });
  };

  const pushBounded = (value: string, absoluteFrom: number): void => {
    if (!value) {
      return;
    }
    if (value.length <= maxChunkLength) {
      pushExact(value, absoluteFrom);
      return;
    }
    let cursor = 0;
    while (cursor < value.length) {
      if (value.length - cursor <= maxChunkLength) {
        pushExact(value.slice(cursor), absoluteFrom + cursor);
        return;
      }
      let splitAt = value.lastIndexOf(' ', cursor + maxChunkLength);
      if (splitAt <= cursor) {
        splitAt = cursor + maxChunkLength;
      }
      pushExact(value.slice(cursor, splitAt), absoluteFrom + cursor);
      cursor = splitAt;
      while (cursor < value.length && value[cursor] === ' ') {
        cursor += 1;
      }
    }
  };

  let searchFrom = 0;
  const paragraphs = trimmed.split(/\n+/);
  for (const paragraph of paragraphs) {
    const relativeIndex = trimmed.indexOf(paragraph, searchFrom);
    const absoluteFrom = leadingWhitespace + relativeIndex;
    searchFrom = relativeIndex + paragraph.length;

    if (paragraph.length <= maxChunkLength) {
      pushBounded(paragraph, absoluteFrom);
      continue;
    }

    const sentences = paragraph.match(/[^.!?…]+[.!?…]+|[^.!?…]+$/g) ?? [paragraph];
    let buffer = '';
    let bufferFrom = absoluteFrom;
    let sentenceSearch = 0;
    for (const sentence of sentences) {
      const next = sentence.trim();
      if (!next) {
        continue;
      }
      const sentenceIndex = paragraph.indexOf(next, sentenceSearch);
      sentenceSearch = sentenceIndex + next.length;
      const nextFrom = absoluteFrom + sentenceIndex;
      if (!buffer) {
        buffer = next;
        bufferFrom = nextFrom;
        continue;
      }
      const candidate = `${buffer} ${next}`;
      if (candidate.length <= maxChunkLength) {
        buffer = candidate;
        continue;
      }
      pushBounded(buffer, bufferFrom);
      buffer = next;
      bufferFrom = nextFrom;
    }
    if (buffer) {
      pushBounded(buffer, bufferFrom);
    }
  }

  return chunks;
};

export const pickDocumentSpeechVoice = (
  voices: SpeechSynthesisVoice[],
  lang: string,
  voiceURI?: string | null,
): SpeechSynthesisVoice | null => {
  if (voices.length === 0) {
    return null;
  }
  if (voiceURI) {
    const byUri = voices.find((voice) => voice.voiceURI === voiceURI);
    if (byUri) {
      return byUri;
    }
  }
  const ranked = filterDocumentSpeechVoicesForLang(voices, lang);
  return ranked[0] ?? null;
};

let activeUtterance: SpeechSynthesisUtterance | null = null;

const isCancelLikeSpeechError = (error: unknown): boolean =>
  error === 'interrupted' || error === 'canceled' || error === 'cancelled';

const speakNextChunk = (sessionId: number): void => {
  if (sessionId !== speakSessionId || !isDocumentSpeechSupported()) {
    return;
  }

  if (nextChunkIndex >= chunks.length) {
    activeUtterance = null;
    setStatus('ended');
    return;
  }

  const chunk = chunks[nextChunkIndex];
  nextChunkIndex += 1;
  spokenPlainRange = { from: chunk.plainFrom, to: chunk.plainTo };
  notifyListeners();

  const utterance = new SpeechSynthesisUtterance(chunk.text);
  utterance.lang = preferences.lang;
  utterance.rate = preferences.rate;
  utterance.volume = preferences.volume;
  const voice = pickDocumentSpeechVoice(
    window.speechSynthesis.getVoices(),
    preferences.lang,
    preferences.voiceURI,
  );
  if (voice) {
    utterance.voice = voice;
  }

  utterance.onend = () => {
    if (sessionId !== speakSessionId) {
      return;
    }
    activeUtterance = null;
    speakNextChunk(sessionId);
  };
  utterance.onerror = (event: SpeechSynthesisErrorEvent) => {
    if (sessionId !== speakSessionId) {
      return;
    }
    if (isCancelLikeSpeechError(event.error)) {
      return;
    }
    activeUtterance = null;
    chunks = [];
    nextChunkIndex = 0;
    setStatus('idle');
  };

  activeUtterance = utterance;
  setStatus('playing');
  window.setTimeout(() => {
    if (sessionId !== speakSessionId || !isDocumentSpeechSupported()) {
      return;
    }
    window.speechSynthesis.speak(utterance);
  }, 0);
};

const rewindToCurrentChunk = (): void => {
  nextChunkIndex = Math.max(0, nextChunkIndex - 1);
};

const restartFromCurrentChunk = (): void => {
  if (
    !isDocumentSpeechSupported()
    || (status !== 'playing' && status !== 'paused')
    || chunks.length === 0
  ) {
    return;
  }
  speakSessionId += 1;
  const sessionId = speakSessionId;
  window.speechSynthesis.cancel();
  activeUtterance = null;
  rewindToCurrentChunk();
  speakNextChunk(sessionId);
};

export const stopDocumentSpeech = (): void => {
  speakSessionId += 1;
  chunks = [];
  nextChunkIndex = 0;
  activeUtterance = null;
  if (isDocumentSpeechSupported()) {
    window.speechSynthesis.cancel();
  }
  setStatus('idle');
};

export const pauseDocumentSpeech = (): void => {
  if (!isDocumentSpeechSupported() || status !== 'playing') {
    return;
  }
  speakSessionId += 1;
  rewindToCurrentChunk();
  window.speechSynthesis.cancel();
  activeUtterance = null;
  setStatus('paused');
};

export const resumeDocumentSpeech = (): void => {
  if (!isDocumentSpeechSupported() || chunks.length === 0) {
    return;
  }
  if (status === 'ended') {
    nextChunkIndex = 0;
    speakNextChunk(speakSessionId);
    return;
  }
  if (status !== 'paused') {
    return;
  }
  const sessionId = speakSessionId;
  speakNextChunk(sessionId);
};

export const setDocumentSpeechPreferences = (
  partial: Partial<DocumentSpeechPreferences>,
): DocumentSpeechPreferences => {
  const previous = preferences;
  preferences = normalizePreferences({ ...preferences, ...partial });
  persistPreferences(preferences);
  notifyListeners();

  const shouldRestart =
    (status === 'playing' || status === 'paused')
    && (
      previous.rate !== preferences.rate
      || previous.volume !== preferences.volume
      || previous.lang !== preferences.lang
      || previous.voiceURI !== preferences.voiceURI
    );
  if (shouldRestart) {
    restartFromCurrentChunk();
  }
  return preferences;
};

export const cycleDocumentSpeechRate = (): DocumentSpeechPreferences =>
  setDocumentSpeechPreferences({
    rate: getNextDocumentSpeechRatePreset(preferences.rate),
  });

export const ensureDocumentSpeechLanguage = (lang: string): void => {
  if (typeof window !== 'undefined' && window.localStorage.getItem(DOCUMENT_SPEECH_STORAGE_KEY)) {
    return;
  }
  if (preferences.lang === lang) {
    return;
  }
  setDocumentSpeechPreferences({ lang });
};

export const speakDocumentText = (
  text: string,
  options: SpeakDocumentTextOptions = {},
): boolean => {
  if (!isDocumentSpeechSupported()) {
    return false;
  }

  const nextChunks = chunkDocumentSpeechTextWithOffsets(text);
  if (nextChunks.length === 0) {
    return false;
  }

  speakSessionId += 1;
  const sessionId = speakSessionId;
  activeUtterance = null;
  spokenPlainRange = null;
  if (isDocumentSpeechSupported()) {
    window.speechSynthesis.cancel();
  }
  chunks = nextChunks;
  nextChunkIndex = 0;
  sourceRange = options.sourceRange ?? null;
  speakNextChunk(sessionId);
  return true;
};

export const __resetDocumentSpeechForTests = (): void => {
  speakSessionId += 1;
  chunks = [];
  nextChunkIndex = 0;
  sourceRange = null;
  spokenPlainRange = null;
  activeUtterance = null;
  status = 'idle';
  preferences = { ...DEFAULT_DOCUMENT_SPEECH_PREFERENCES };
};
